import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import { SvgDelete, SvgEdit } from "components/svg";
import { useState } from "react";
import { Controller, Control, useWatch } from "react-hook-form";
import useTranslation from "utils/hooks/useTranslation";
import { OptionBar } from "../OptionBar";
import { InstallationType } from "components/EtudeForm/hooks/useGetSolutionsQuery";
import { ServiceNames } from "components/EtudeForm/Solutions/Solutions";
import { useCurrentEtudeData } from "components/EtudeForm/hooks/useCurrentEtudeData";

interface CircuitItemProps {
  control: Control<any>;
  name: string;
  onDelete: () => void;
  allData?: any;
  index: number;
  service?: string;
}

export const CircuitItem: React.FC<CircuitItemProps> = ({
  control,
  name,
  onDelete,
  allData,
  index,
  service,
}) => {
  const [isNameEdit, setIsNameEdit] = useState(false);
  const { t } = useTranslation();
  const nameVal = useWatch({ control, name: `${name}.name`, exact: true });
  const installationType = useWatch({ control, name: "installationType", exact: true });
  const { installationType: currentInstallationType } = useCurrentEtudeData();

  return (
    <group
      data-gap="10"
      data-space="20"
      data-border=""
      data-radius="special"
      data-length="480"
    >
      <group>
        <group data-gap="10" data-width="auto">
          {isNameEdit ? (
            <Input label="Name" control={control} name={`${name}.name`} />
          ) : (
            <text data-text-size="large" data-color="grey-color-dark">
              {nameVal}
            </text>
          )}
          <Button
            micro
            rounded
            outline
            data-color="grey-color-dark"
            onClick={() => setIsNameEdit((prev) => !prev)}
          >
            <icon data-svg-color="grey-color-dark" data-svg-size="16">
              <SvgEdit />
            </icon>
          </Button>
        </group>
        {index !== 0 && (
          <group data-gap="10" data-width="auto" data-position="right">
            <Button
              onClick={onDelete}
              micro
              rounded
              outline
              data-color="error-color"
            >
              <icon data-svg-color="error-color" data-svg-size="16">
                <SvgDelete />
              </icon>
            </Button>
          </group>
        )}
      </group>
      <group data-gap="10" data-direction="column">
        <Controller
          name={`${name}.type`}
          control={control}
          defaultValue={undefined}
          render={({ field, fieldState }) => {
            return (
              <Input
                type={InputType.Select}
                label="FO_SIMULATION_CIRCUIT_TYPE"
                options={[
                  { text: t("FO_SIMULATION_REGULATED"), value: 1 },
                  { text: t("FO_SIMULATION_CONSTANT"), value: 2 },
                ]}
                value={field.value}
                onChange={(val) => field.onChange(val ?? undefined)}
                onBlur={field.onBlur}
                error={fieldState.error}
                required
              />
            );
          }}
        />
        {(installationType || currentInstallationType) === InstallationType.hybride && (
          <OptionBar
            label={t("FO_SIMULATION_UNDERFLOOR_HEATING")}
            control={control}
            name={`${name}.underfloorHeating`}
            dataLength="440"
            options={[
              {
                value: "yes",
                text: t("FO_ETU_YES"),
              },
              {
                value: "no",
                text: t("FO_ETU_NO"),
              },
            ]}
          />
        )}
        <Input
          label="FO_SIMULATION_CIRCUIT_POWER"
          unit={t("FO_UNIT_KW")}
          control={control}
          helpText={
            service == ServiceNames.DoubleService
              ? t("FO_HELP_POWER_CIRC_DS")
              : t("FO_HELP_POWER_CIRC")
          }
          name={`${name}.puissance`}
          type={InputType.Number}
          minValue={allData?.[`${name}.puissance`]?.minimum}
          maxValue={allData?.[`${name}.puissance`]?.maximum}
        />
        <Input
          label="FRONT_SIMULATION_POWER_FACTOR"
          control={control}
          name={`${name}.surpuissance`}
          type={InputType.Number}
          unit={t("FO_UNIT_PERCENT")}
          helpText={t("FO_HELP_POWER_FACTOR")}
          minValue={allData?.[`${name}.surpuissance`]?.minimum}
          maxValue={allData?.[`${name}.surpuissance`]?.maximum}
        />
        <Input
          label="FO_SIMULATION_START_TEMPERATURE"
          unit={t("FO_UNIT_C")}
          control={control}
          name={`${name}.startTemperature`}
          type={InputType.Number}
          minValue={allData?.[`${name}.startTemperature`]?.minimum}
          maxValue={allData?.[`${name}.startTemperature`]?.maximum}
        />
        <Input
          label="FO_SIMULATION_DELTA_TEMPERATURE"
          unit={t("FO_UNIT_C")}
          control={control}
          name={`${name}.delta_t`}
          type={InputType.Number}
          minValue={allData?.[`${name}.delta_t`]?.minimum}
          maxValue={allData?.[`${name}.delta_t`]?.maximum}
        />

      </group>
    </group>
  );
};
